// Account emails (password reset, sign-in link, email verification).
//
// They're sent by our backend (email_sender: Routes/auth.js) so they use the
// guiders.pt design and link to our own /auth/action page. If the backend
// can't be reached, Firebase's built-in emails are sent instead, so these
// never stop working.
import firebase from "firebase/app";
import "firebase/auth";
import { i18n } from "@/i18n";

const API = process.env.VUE_APP_API_URL;

// Remembered between asking for a sign-in link and opening it (Firebase
// needs the email again to finish the sign-in)
export const EMAIL_FOR_SIGN_IN_KEY = "guiders.emailForSignIn";

const language = () => (i18n && i18n.locale) || "en";
const siteUrl = (path) => `${window.location.origin}${path}`;

async function post(path, body, headers = {}) {
  const response = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(body),
  });
  let data = {};
  try {
    data = await response.json();
  } catch (e) {
    // empty or non-JSON answer
  }
  // 404 = backend not deployed yet: treat like "unreachable" (fallback)
  if (response.status === 404) throw Object.assign(new Error("not deployed"), { fallback: true });
  if (!response.ok) throw Object.assign(new Error(data.message || "Request failed"), { userMessage: data.message });
  return data;
}

// Network failure or old backend -> use Firebase's own email instead
const shouldFallBack = (error) => error.fallback || error instanceof TypeError;

export async function requestPasswordReset(email) {
  try {
    await post("/auth/password-reset", { email, lang: language() });
  } catch (error) {
    if (!shouldFallBack(error)) throw error;
    try {
      await firebase.auth().sendPasswordResetEmail(email, { url: siteUrl("/login") });
    } catch (firebaseError) {
      // Don't reveal whether an account exists
      if (firebaseError.code !== "auth/user-not-found") throw firebaseError;
    }
  }
}

export async function requestSignInLink(email) {
  try {
    await post("/auth/sign-in-link", { email, lang: language() });
  } catch (error) {
    if (!shouldFallBack(error)) throw error;
    await firebase.auth().sendSignInLinkToEmail(email, { url: siteUrl("/auth/action"), handleCodeInApp: true });
  }
  try {
    window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
  } catch (e) {
    // storage blocked: the action page will ask for the email
  }
}

export async function sendVerificationEmail() {
  const user = firebase.auth().currentUser;
  if (!user || user.emailVerified) return;
  try {
    const token = await user.getIdToken();
    await post("/auth/email-verification", { lang: language() }, { Authorization: `Bearer ${token}` });
  } catch (error) {
    if (!shouldFallBack(error)) throw error;
    await user.sendEmailVerification({ url: siteUrl("/login") });
  }
}

// Authorization header for backend calls made on behalf of the signed-in user
export async function authHeaders() {
  const user = firebase.auth().currentUser;
  if (!user) return {};
  return { Authorization: `Bearer ${await user.getIdToken()}` };
}
